import ExcelJS from "exceljs";
import {
  downloadWorkbook,
  fileMonthLabel,
  formatPayMonthLabel,
  getCompanyName,
  styleLightHeader,
  applyDataBorder,
  moneyFmt,
  addTitleBlock,
  applyHeaderBasedColumnWidths,
  REPORT_COLORS,
} from "./payrollReportExcelShared";

function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
}

function styleHeaderRow(row, fillArgb = REPORT_COLORS.recon) {
  row.eachCell((cell) => styleLightHeader(cell, fillArgb));
}

function styleDataRow(row, moneyCols = []) {
  row.eachCell((cell, colNumber) => {
    applyDataBorder(cell);
    if (moneyCols.includes(colNumber)) moneyFmt(cell);
  });
}

/** Cumulative payment ledger — Salary & F&F columns, obligation month + payment date. */
export async function exportPaymentLedgerExcel({ rows = [], payMonth = "", contextLabel = "" } = {}) {
  if (!rows.length) throw new Error("No payment ledger rows to export");

  const companyName = getCompanyName();
  const monthLabel = payMonth ? formatPayMonthLabel(payMonth) : contextLabel || "All";
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  const headers = [
    "Sl. No",
    "Payment Date",
    "Obligation Month",
    "Emp Code",
    "Employee",
    "Stream",
    "Action",
    "Salary Paid",
    "F&F Paid",
    "Recovery Applied",
    "Hold Left",
    "Gross Applied",
    "Notes",
  ];
  const ws = wb.addWorksheet("Payment Ledger", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  addTitleBlock(
    ws,
    companyName,
    `Payment Ledger — ${monthLabel}`,
    `Generated: ${new Date().toLocaleString("en-IN")}  |  ${rows.length} line(s)`,
    headers.length
  );

  styleHeaderRow(ws.addRow(headers));

  rows.forEach((r, i) => {
    const row = ws.addRow([
      i + 1,
      r.paymentDate || "",
      r.obligationPayMonth || "",
      r.empCode || "",
      r.empName || "",
      r.stream || "",
      r.action || "",
      round2(r.salaryPaid),
      round2(r.fnfPaid),
      round2(r.recoveryApplied),
      round2(r.holdLeft),
      round2(r.grossApplied),
      r.notes || r.batchNotes || "",
    ]);
    styleDataRow(row, [8, 9, 10, 11, 12]);
  });

  applyHeaderBasedColumnWidths(ws, headers);
  const fileLabel = payMonth ? fileMonthLabel(payMonth) : "ledger";
  return downloadWorkbook(wb, `Payment Ledger ${fileLabel}.xlsx`);
}

/** Unpaid Salary report — open unpaid / hold / recovery with month summary + accumulation. */
export async function exportUnpaidSalaryExcel({
  rows = [],
  monthSummary = [],
  accumulation = [],
  fromMonth = "",
  toMonth = "",
} = {}) {
  const companyName = getCompanyName();
  const rangeLabel =
    fromMonth || toMonth
      ? `${fromMonth ? formatPayMonthLabel(fromMonth) : "…"} – ${toMonth ? formatPayMonthLabel(toMonth) : "…"}`
      : "All open";
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  const openHeaders = [
    "Sl. No",
    "Obligation Month",
    "Emp Code",
    "Employee",
    "Salary Unpaid",
    "F&F Unpaid",
    "Salary Hold",
    "F&F Hold",
    "Salary Recovery",
    "F&F Recovery",
    "Total Unpaid",
    "Total Hold",
    "Total Recovery",
  ];
  const openWs = wb.addWorksheet("Open Unpaid", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  addTitleBlock(
    openWs,
    companyName,
    `Unpaid Salary & F&F — ${rangeLabel}`,
    `Generated: ${new Date().toLocaleString("en-IN")}  |  ${rows.length} employee-month(s)`,
    openHeaders.length
  );
  styleHeaderRow(openWs.addRow(openHeaders));
  rows.forEach((r, i) => {
    const row = openWs.addRow([
      i + 1,
      r.obligationPayMonth || "",
      r.empCode || "",
      r.empName || "",
      round2(r.salaryUnpaid),
      round2(r.fnfUnpaid),
      round2(r.salaryHold),
      round2(r.fnfHold),
      round2(r.salaryRecovery),
      round2(r.fnfRecovery),
      round2(r.totalUnpaid),
      round2(r.totalHold),
      round2(r.totalRecovery),
    ]);
    styleDataRow(row, [5, 6, 7, 8, 9, 10, 11, 12, 13]);
  });
  applyHeaderBasedColumnWidths(openWs, openHeaders);

  const monthHeaders = [
    "Obligation Month",
    "Employees",
    "Salary Unpaid",
    "F&F Unpaid",
    "Total Unpaid",
    "Total Hold",
    "Total Recovery",
  ];
  const monthWs = wb.addWorksheet("Month Summary");
  addTitleBlock(
    monthWs,
    companyName,
    `Month Summary — ${rangeLabel}`,
    `Generated: ${new Date().toLocaleString("en-IN")}`,
    monthHeaders.length
  );
  styleHeaderRow(monthWs.addRow(monthHeaders));
  monthSummary.forEach((m) => {
    const row = monthWs.addRow([
      m.obligationPayMonth || "",
      m.employees || 0,
      round2(m.salaryUnpaid),
      round2(m.fnfUnpaid),
      round2(m.totalUnpaid),
      round2(m.totalHold),
      round2(m.totalRecovery),
    ]);
    styleDataRow(row, [3, 4, 5, 6, 7]);
  });
  applyHeaderBasedColumnWidths(monthWs, monthHeaders);

  const ageHeaders = [
    "Obligation Month",
    "Month Unpaid",
    "Month Hold",
    "Month Recovery",
    "Cumulative Unpaid",
    "Cumulative Hold",
    "Cumulative Recovery",
  ];
  const ageWs = wb.addWorksheet("Aging Accumulation");
  addTitleBlock(
    ageWs,
    companyName,
    `Unpaid Accumulation — ${rangeLabel}`,
    "Running totals of open unpaid / hold / recovery by obligation month",
    ageHeaders.length
  );
  styleHeaderRow(ageWs.addRow(ageHeaders));
  accumulation.forEach((m) => {
    const row = ageWs.addRow([
      m.obligationPayMonth || "",
      round2(m.totalUnpaid),
      round2(m.totalHold),
      round2(m.totalRecovery),
      round2(m.cumulativeUnpaid),
      round2(m.cumulativeHold),
      round2(m.cumulativeRecovery),
    ]);
    styleDataRow(row, [2, 3, 4, 5, 6, 7]);
  });
  applyHeaderBasedColumnWidths(ageWs, ageHeaders);

  return downloadWorkbook(wb, `Unpaid Salary ${fileMonthLabel(fromMonth || toMonth || "open")}.xlsx`);
}
